/**
 * Keeping this device's copy of the countdown in step with everyone else's.
 *
 * ---------------------------------------------------------------------------
 * WHY POLLING
 * ---------------------------------------------------------------------------
 * The obvious answer is a WebSocket or SSE, and the obvious answer needs a
 * Durable Object to fan out from - a stateful, always-warm, paid primitive, to
 * push one integer that changes perhaps twice a year. Polling a 120-byte JSON
 * response is the right size of solution for the size of the problem.
 *
 * The cost of polling is request count, and that is what the schedule below is
 * about. A tab left open on a second monitor for a week must not quietly spend
 * the daily Workers budget, but the laptop you just walked over to must catch
 * up immediately. So:
 *
 *   - hidden tab: no polling at all. There is nothing to see, and the browser
 *     throttles the timer to once a minute anyway, so the requests would be
 *     both useless and unpredictable.
 *   - becoming visible, regaining focus, coming back online: fetch at once.
 *     This is the case that actually matters. Walking from the phone to the
 *     laptop *is* a visibility change, so the laptop is right by the time you
 *     have looked at it.
 *   - visible and recently touched: `ACTIVE_MS`, so a second device in the same
 *     room follows along a few seconds behind.
 *   - visible and untouched for a while: `IDLE_MS`. Nobody is comparing two
 *     screens they are not looking at.
 *
 * ---------------------------------------------------------------------------
 * WHY THE COUNTDOWN NEVER STOPS
 * ---------------------------------------------------------------------------
 * Every failure path here still leaves a countdown on the screen, falling back
 * to the original target compiled into the bundle. A blank page or an error
 * where the digits should be is a worse answer to a dropped connection than
 * numbers that are a few seconds stale - especially since, for six years out of
 * six, the fallback and the truth are the same number.
 */

import { adoptSample } from './clock.ts'
import { defaultState, isMode, stateForMode, type CountdownState, type Mode } from './shared/countdown.ts'

const ENDPOINT = '/api/state'

/*
 * The polling schedule, in three tiers.
 *
 * The numbers are a budget question. Cloudflare's free Workers allowance is
 * 100,000 requests a day, and a tab left open on a second monitor is the thing
 * that spends it: at four seconds a single forgotten tab is 21,600 requests a
 * day all by itself, and three of them would eat two thirds of the allowance
 * showing a number nobody is reading.
 *
 * So the rate follows attention rather than being constant. `IDLE_MS` is the
 * one that matters for the feel of the site - it is how long a second screen
 * sitting next to you takes to follow a press on the first - and eight seconds
 * is the most that felt like "they are in sync" rather than "the other one is
 * behind". `DORMANT_MS` catches the tab that has genuinely been abandoned:
 * still visible on some monitor, untouched for half an hour, worth a request a
 * minute and no more.
 *
 * None of this applies to the case that actually happens most, which is walking
 * over to the laptop and looking at it - that is a focus event, and a focus
 * event fetches immediately.
 */
const ACTIVE_MS = 3_000
const IDLE_MS = 8_000
const DORMANT_MS = 60_000
/** How long after a click or keypress this device still counts as "in use". */
const ACTIVE_WINDOW_MS = 2 * 60_000
/** How long a visible but untouched tab keeps the middle rate before dropping. */
const DORMANT_AFTER_MS = 30 * 60_000
const MAX_BACKOFF_MS = 60_000

export type Link =
  /** Nothing has come back yet. */
  | 'connecting'
  /** In sync with the shared state. */
  | 'live'
  /** Server answered, but has no database, so presses cannot be shared. */
  | 'unshared'
  /** Cannot reach the server. Showing the last known, or the built-in, target. */
  | 'offline'

export interface Sync {
  state: CountdownState
  link: Link
  /** Set when the last press or fetch failed, for the status line. */
  problem: string | null
}

type Listener = (sync: Sync) => void

interface ApiPayload {
  ok?: boolean
  state?: { mode?: unknown; target?: unknown; changedAt?: unknown }
  now?: unknown
  stored?: unknown
  error?: unknown
}

function parseState(raw: ApiPayload['state']): CountdownState | null {
  if (!raw || !isMode(raw.mode) || typeof raw.target !== 'number' || typeof raw.changedAt !== 'number') return null
  if (!Number.isFinite(raw.target) || !Number.isFinite(raw.changedAt)) return null
  return { mode: raw.mode, target: raw.target, changedAt: raw.changedAt }
}

export function createSync(): {
  subscribe: (listener: Listener) => void
  press: (mode: Mode) => Promise<void>
  start: () => void
  current: () => Sync
} {
  let current: Sync = { state: defaultState(), link: 'connecting', problem: null }
  const listeners: Listener[] = []
  let timer: number | undefined
  let failures = 0
  // A tab that has just been opened is being looked at, whether or not anyone
  // has clicked anything yet. Starting this at 0 put every fresh page straight
  // into the dormant tier and made the first minute of every visit the slowest.
  let lastInteraction = Date.now()
  let inFlight = false

  const emit = (): void => {
    for (const listener of listeners) listener(current)
  }

  const set = (next: Partial<Sync>): void => {
    current = { ...current, ...next }
    emit()
  }

  const delay = (): number => {
    if (failures > 0) return Math.min(MAX_BACKOFF_MS, ACTIVE_MS * 2 ** failures)
    const since = Date.now() - lastInteraction
    if (since < ACTIVE_WINDOW_MS) return ACTIVE_MS
    return since < DORMANT_AFTER_MS ? IDLE_MS : DORMANT_MS
  }

  const schedule = (): void => {
    window.clearTimeout(timer)
    if (document.visibilityState === 'hidden') return
    timer = window.setTimeout(() => void refresh(), delay())
  }

  /**
   * Applies a payload from either the GET or the POST. Both return the same
   * shape on purpose: a press and a poll are the same event as far as the rest
   * of the page is concerned, so there is only one code path that can be wrong.
   */
  const apply = (data: ApiPayload, sentAt: number): void => {
    const receivedAt = Date.now()
    if (typeof data.now === 'number') adoptSample(data.now, sentAt, receivedAt)
    const state = parseState(data.state)
    set({
      state: state ?? current.state,
      link: data.stored === true ? 'live' : 'unshared',
      problem: null
    })
  }

  const refresh = async (): Promise<void> => {
    if (inFlight) return
    inFlight = true
    const sentAt = Date.now()
    try {
      // `cache: 'no-store'` as well as the server's headers. Belt and braces,
      // because a cached poll is invisible: the request succeeds, the state
      // looks fine, and the button someone pressed simply never arrives.
      const response = await fetch(ENDPOINT, { cache: 'no-store', headers: { accept: 'application/json' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      apply((await response.json()) as ApiPayload, sentAt)
      failures = 0
    } catch (error) {
      failures += 1
      set({ link: 'offline', problem: describe(error) })
    } finally {
      inFlight = false
      schedule()
    }
  }

  const press = async (mode: Mode): Promise<void> => {
    lastInteraction = Date.now()
    // Show the result before the network confirms it. A button that does
    // nothing for 300 ms gets pressed again, and the second press on "start
    // over" restarts a countdown that had already restarted.
    set({ state: stateForMode(mode, Date.now()), problem: null })
    const sentAt = Date.now()
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ mode })
      })
      const data = (await response.json().catch(() => ({}))) as ApiPayload
      if (!response.ok || data.ok !== true) throw new Error(String(data.error ?? `HTTP ${response.status}`))
      // The server's target replaces the predicted one. They agree to within
      // the round trip, but the server's is the one the other devices got, and
      // two devices differing by 200 ms in the seconds column is visible when
      // they are lying next to each other.
      apply(data, sentAt)
      failures = 0
    } catch (error) {
      set({ link: 'offline', problem: `That press did not reach the server (${describe(error)}).` })
      failures = Math.max(failures, 1)
    } finally {
      schedule()
    }
  }

  const noteInteraction = (): void => {
    lastInteraction = Date.now()
  }

  const start = (): void => {
    void refresh()
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        failures = 0
        noteInteraction()
        void refresh()
      } else {
        window.clearTimeout(timer)
      }
    })
    window.addEventListener('focus', () => {
      // Coming back to the window is attention, even without a click. This is
      // the walking-over-to-the-laptop case, and it is the one that has to feel
      // instant.
      noteInteraction()
      void refresh()
    })
    window.addEventListener('online', () => {
      failures = 0
      void refresh()
    })
    window.addEventListener('pointerdown', noteInteraction, { passive: true })
    window.addEventListener('keydown', noteInteraction, { passive: true })
  }

  return {
    subscribe: (listener) => {
      listeners.push(listener)
      listener(current)
    },
    press,
    start,
    current: () => current
  }
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
